<template>
  <div>
    <div class="relative flex items-center border-b">
      <FontAwesomeIcon
        v-if="loading"
        :icon="faSpinnerThird"
        class="pointer-events-none absolute left-4 top-4 size-4 animate-spin text-content-subtle"
        aria-hidden="true"
      />
      <FontAwesomeIcon
        v-else
        :icon="faMagnifyingGlass"
        class="pointer-events-none absolute left-4 top-4 size-4 text-content-subtle"
        aria-hidden="true"
      />
      <input
        v-model="query"
        type="text"
        class="flex h-12 w-full border-0 bg-transparent py-3 pl-10 pr-2 text-sm outline-hidden placeholder:text-content-subtle focus:border-0 focus:outline-hidden focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
        placeholder="Search..."
        autocomplete="off"
        autocorrect="off"
        spellcheck="false"
        @keydown.down.prevent="moveActiveItem(activeItemNext)"
        @keydown.up.prevent="moveActiveItem(activeItemPrevious)"
        @keydown.enter.prevent="handleEnterKey"
        @keydown.tab.prevent="handleTabKey"
      >
      <Keyboard
        class="mr-4 cursor-pointer"
        @click="closeSearchPalette"
      >
        Esc
      </Keyboard>
    </div>
    <ul
      ref="resultsList"
      class="max-h-[60vh] divide-y divide-line overflow-y-auto"
    >
      <template
        v-for="group in searchResults.results"
        :key="group.name"
      >
        <li
          class="p-2"
        >
          <div class="flex justify-between px-4 py-2 text-xs">
            <SpectreHeading
              as="h3"
              size="sm"
              class="uppercase"
            >
              {{ group.name }}
            </SpectreHeading>
            <SpectreTextLink
              :href="group.viewAllUrl"
              :trailing-icon="faArrowUpRightFromSquare"
            >
              View all
            </SpectreTextLink>
          </div>
          <Collapse
            speed="fast"
            :collapsed="group.searchResults.length === 0 || query === ''"
          >
            <ul
              class="text-sm text-content-secondary"
            >
              <li
                v-for="item in group.searchResults"
                :key="item.id"
                :data-search-result="item.id"
                :class="[
                  'relative flex cursor-pointer select-none items-center rounded-md px-4 py-2',
                  isActiveItem(item) ? 'border border-line-strong bg-surface-subtle text-primary-content' : 'border border-transparent',
                  // Keep a labelled row's badge clear of the Enter/Tab hint drawn over its right end.
                  isActiveItem(item) && item.branchType ? (item.shadowUrl ? 'pr-64' : 'pr-36') : '',
                ]"
                @click="view(item.viewUrl)"
                @mousemove="setActiveItem(item)"
              >
                <div :class="['truncate font-medium', item.branchType ? 'min-w-0' : 'flex-auto']">
                  {{ item.name }}
                </div>
                <SpectreBadge
                  v-if="item.branchType"
                  data-branch-type
                  color="default"
                  class="ml-2 shrink-0"
                >
                  {{ item.branchType }}
                </SpectreBadge>
                <div
                  v-if="isActiveItem(item)"
                  class="absolute right-4 flex items-center space-x-3"
                >
                  <div
                    class="ml-3 flex items-center space-x-1 text-xs font-semibold text-content-secondary hover:text-primary-hover"
                    @click.stop="view(item.viewUrl)"
                  >
                    <Keyboard>Enter</Keyboard>
                    <span>to View</span>
                  </div>
                  <template v-if="item.shadowUrl">
                    <div class="h-9 w-px bg-line-strong" />
                    <div
                      class="flex items-center space-x-1 text-xs font-semibold text-content-secondary hover:text-primary-hover"
                      @click.stop="shadow(item.shadowUrl)"
                    >
                      <Keyboard>Tab</Keyboard>
                      <span>to Shadow</span>
                    </div>
                  </template>
                </div>
              </li>
            </ul>
          </Collapse>
        </li>
      </template>
    </ul>
  </div>
</template>
<script setup lang="ts">
import { faArrowUpRightFromSquare, faSpinnerThird } from '@awesome.me/kit-644ec404bf/icons/classic/solid';
import { faMagnifyingGlass } from '@awesome.me/kit-644ec404bf/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useGlobalSearch from '@/Composables/useGlobalSearch';
import Keyboard from '@/Components/Shared/Keyboard.vue';
import Collapse from '@/Components/Shared/Collapse.vue';
import { SpectreBadge, SpectreHeading, SpectreTextLink } from '@agentsoftware/spectre-ui';
import { nextTick, useTemplateRef } from 'vue';

const {
  closeSearchPalette,
  loading,
  searchResults,
  query,
  activeItem,
  setActiveItem,
  isActiveItem,
  view,
  shadow,
  handleEnterKey,
  handleTabKey,
  activeItemNext,
  activeItemPrevious,
} = useGlobalSearch();

// An account and branch search can return more rows than fit, so keep the row picked with
// the arrow keys in view. Only the keys scroll: hovering a row half out of view must not move
// the list under the pointer.
const resultsList = useTemplateRef<HTMLUListElement>('resultsList');

const moveActiveItem = async (move: () => void) => {
  move();
  await nextTick();
  resultsList.value
    ?.querySelector(`[data-search-result="${activeItem.value}"]`)
    ?.scrollIntoView({ block: 'nearest' });
};

</script>
